import { Box } from '@mui/material'
import { Login, Logout, MeetingRoom, SwapHoriz } from '@mui/icons-material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { GateDirection } from '@yard/app/api'
import { observer } from 'mobx-react-lite'
import { ReactNode } from 'react'
import { NodeProps } from 'reactflow'
import { LayoutObjectWithActions } from '../../models/yard-view.model'
import { FocusWrapper } from '../molecules/FocusWrapper'
import { LayoutObjectHeader } from '../molecules/LayoutObjectHeader'
import { NodeResizer } from '../molecules/NodeResizer'
import { RotatableNodeWrapper } from '../molecules/RotatableNodeWrapper'

const getDirectionIcon = (direction?: GateDirection | null): ReactNode => {
  switch (direction) {
    case GateDirection.In:
      return <Login />
    case GateDirection.Out:
      return <Logout />
    case GateDirection.Both:
      return <SwapHoriz />
    default:
      return <MeetingRoom />
  }
}

export const GateNode = observer(({ data }: NodeProps<LayoutObjectWithActions>) => {
  const {
    id,
    name,
    description,
    focused,
    showNodeHeaders,
    isEditable,
    layoutPosition,
    direction,
    active,
    onRotate,
    onFocus,
    onEdit,
    onDelete,
    onResize,
  } = data

  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const directionIcon = getDirectionIcon(direction)

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <RotatableNodeWrapper
        rotation={layoutPosition.rotation}
        enabled={focused && isEditable}
        onRotationEnd={angle => onRotate(id, angle)}
        parentDeterminesSize
        header={
          focused || showNodeHeaders ? (
            <LayoutObjectHeader
              id={id}
              name={name}
              description={description}
              focused={focused}
              isEditable={isEditable}
              icon={directionIcon}
              onEdit={onEdit}
              onDelete={onDelete}
              deleteDisabled={active === true}
              deleteDisabledTooltip={t(
                'gateDeactivateBeforeDeleting',
                'Deactivate before deleting',
              )}
            />
          ) : undefined
        }
      >
        <FocusWrapper
          focused={focused}
          isEditable={isEditable}
          onFocus={() => onFocus(id)}
          height='100%'
          width='100%'
          parentDeterminesSize
          hideGrayBackground
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'stretch',
              backgroundColor: theme.palette.grey[600],
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: 14, backgroundColor: theme.palette.grey[500], flexShrink: 0 }} />
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                '& .MuiSvgIcon-root': { fontSize: '2rem' },
              }}
            >
              {directionIcon}
            </Box>
            <Box sx={{ width: 14, backgroundColor: theme.palette.grey[500], flexShrink: 0 }} />
          </Box>
          <NodeResizer
            isEditable={isEditable && focused}
            onResize={(width, height) => onResize(id, width, height)}
          />
        </FocusWrapper>
      </RotatableNodeWrapper>
    </Box>
  )
})
